import { User, UserSettings } from "oidc-client";
import { getClientConfiguration } from "../../configuration";
import { UserSignInResultDto } from "../models";
import { getUserManager } from "./userManagerHelper";
import { setUserPreferences } from "./userPreferencesHelper";

interface UserProfile {
  id: string;
}

let signedIn: boolean | undefined;
let userProfile: UserProfile | undefined;
let userRoles: string[] | undefined;
let userAuthorizationPolicies: { [key: string]: boolean } | undefined;

export const hasUserActiveSession = (): boolean => {
  const clientStsConfiguration = getClientConfiguration().sts;
  if (!clientStsConfiguration) {
    return false;
  }
  const userSettingsStorageKey = `oidc-user:${clientStsConfiguration.authority}:${clientStsConfiguration.apiClientName}`;
  const userSettingsJson = window.sessionStorage.getItem(userSettingsStorageKey);
  if (!userSettingsJson) {
    return false;
  }
  const userSettings = JSON.parse(userSettingsJson) as UserSettings;
  // If the session has not yet expired return true, otherwise false
  return userSettings.expires_at ? userSettings.expires_at > Date.now() : false;
};

export const isUserSignedIn = () => signedIn;

export const isUserLoading = () => signedIn === undefined;

export const getUserFullName = (user: User) => {
  if (user) {
    return `${user.profile.given_name} ${user.profile.family_name}`;
  }
  return "Unknown User";
};

export const isUserWithUsername = (user: User | undefined, username: string) => {
  if (user && user.profile && user.profile.name) {
    return user.profile.name === username;
  }
  return false;
};

export const isUserInRole = (role: string) => {
  const roles = getUserRoles();
  // If the user is not signed in, or their session has since expired, return no membership...
  if (roles === undefined) {
    return false;
  }
  return roles.findIndex(r => r.toLowerCase() === role.toLowerCase()) !== -1;
};

export const isUserAuthorized = (policyKeys?: string[]) => {
  const authorizationPolicies = getUserAuthorizationPolicies();
  // If the user is not signed in, or their session has since expired, return unauthorized...
  if (authorizationPolicies === undefined) {
    return false;
  }
  // If there are no policies to evaluate, return authorized...
  if (!Array.isArray(policyKeys) || !policyKeys.length) {
    return true;
  }
  // If at least one of the specified policies is satisfied, return authorized, otherwise, unauthorized...
  return policyKeys.some(policyKey => {
    if (authorizationPolicies[policyKey] === undefined) {
      throw Error(`Authorization Policy ${policyKey} is not registered.`);
    }

    return authorizationPolicies[policyKey];
  });
};

export const setUserSignedIn = <T>(userId?: string, signInResult?: UserSignInResultDto<T>) => {
  if (userId && signInResult) {
    signedIn = signInResult.signedIn;
    setUserProfile(userId);
    setUserRoles(signInResult.roles);
    // If no Authorization Policies are in-effect, then initialize authorizationPolicies to an empty object so that
    // we can always return true in isUserAuthorized
    setUserAuthorizationPolicies(signInResult.authorizationPolicies);
    if (signInResult.preferences) {
      setUserPreferences(signInResult.preferences);
    }
  } else {
    signedIn = true;
  }
};

export const setUserSignedOut = () => {
  signedIn = false;
  userProfile = undefined;
  userRoles = undefined;
  userAuthorizationPolicies = undefined;
  // Remove Roles from session storage as applicable...
  if (window.sessionStorage.getItem("roles")) {
    window.sessionStorage.removeItem("roles");
  }
  // Remove Authorization Policies from session storage as applicable...
  if (window.sessionStorage.getItem("authorization_policies")) {
    window.sessionStorage.removeItem("authorization_policies");
  }
};

export const getApiAccessToken = (): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    getUserManager()
      .getUser()
      .then(user => {
        if (!user) {
          reject("User could not be resolved.");
        } else {
          resolve(user.access_token);
        }
      })
      .catch(error => {
        reject(`The following error occurred upon attempting to retrieve the Access Token: ${error}`);
      });
  });
};

export const getUserProfile = () => {
  // If user profile is not cached, retrieve from session storage...
  if (!userProfile) {
    const profile = window.sessionStorage.getItem("profile");
    if (profile) {
      userProfile = JSON.parse(profile) as UserProfile;
    }
  }
  return userProfile;
};

export const getUserRoles = () => {
  // If user roles are not cached, retrieve them from session storage...
  if (!userRoles) {
    const roles = window.sessionStorage.getItem("roles");
    if (roles) {
      userRoles = JSON.parse(roles) as string[];
    }
  }
  return userRoles;
};

export const getUserAuthorizationPolicies = () => {
  // If user authorization policies are not cached, retrieve them from session storage...
  if (!userAuthorizationPolicies) {
    const authorizationPolicies = window.sessionStorage.getItem("authorization_policies");
    if (authorizationPolicies) {
      userAuthorizationPolicies = JSON.parse(authorizationPolicies) as { [key: string]: boolean };
    }
  }
  return userAuthorizationPolicies;
};

const setUserProfile = (userId: string) => {
  userProfile = { id: userId };
  window.sessionStorage.setItem("profile", JSON.stringify(userProfile));
};

const setUserRoles = (roles?: string[] | null) => {
  userRoles = roles || [];
  window.sessionStorage.setItem("roles", JSON.stringify(userRoles));
};

const setUserAuthorizationPolicies = (authorizationPolicies?: { [key: string]: boolean } | null) => {
  userAuthorizationPolicies = authorizationPolicies || {};
  window.sessionStorage.setItem("authorization_policies", JSON.stringify(userAuthorizationPolicies));
};
